import { ZERO_ID, type Id } from './id';

/**
 * 时间 ⇄ 主键区间换算，与后端 Hq.Shared/Ids/IdKit.cs 完全一致（DR-4.3）。
 * 后端 EPOCH 固定 2026-01-01 00:00:00（UTC+8）= 2025-12-31T16:00:00Z。
 */
export const ID_EPOCH_MS = Date.UTC(2025, 11, 31, 16, 0, 0, 0);

/** 批量占号/补录可能微超前，端点额外 +2s 冗余。 */
export const ID_BATCH_TOLERANCE_MS = 2_000;

export type TimeInput = Date | string | number;

const DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?(\.\d{1,7})?$/;

/**
 * 解析后端时间：兼容 ISO-8601（`2026-01-05T14:03:12.345+08:00`）
 * 与数据库口径的无时区格式（`2026-01-05 14:03:12.345`，按本地时区解释）。
 */
export function parseApiTime(value: string | null | undefined): Date | null {
  if (!value) return null;
  const raw = value.trim();
  if (raw === '') return null;
  const normalized = DATE_TIME_PATTERN.test(raw) ? raw.replace(' ', 'T') : raw;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

function toMs(input: TimeInput): number {
  if (input instanceof Date) return input.getTime();
  if (typeof input === 'number') return input;
  const parsed = parseApiTime(input);
  if (!parsed) throw new RangeError(`无法解析时间：${input}`);
  return parsed.getTime();
}

function msToId(ms: number): Id {
  return ms <= 0 ? ZERO_ID : (BigInt(Math.floor(ms)) << 22n).toString();
}

/** 区间起点：`IdRange.Begin`。 */
export function idRangeBegin(time: TimeInput): Id {
  return msToId(toMs(time) - ID_EPOCH_MS);
}

/** 区间终点：`IdRange.End`（末毫秒 +2s 冗余后下包）。 */
export function idRangeEnd(time: TimeInput): Id {
  return msToId(toMs(time) + ID_BATCH_TOLERANCE_MS - ID_EPOCH_MS);
}

export function idRange(start: TimeInput, end: TimeInput): { beginId: Id; endId: Id } {
  return { beginId: idRangeBegin(start), endId: idRangeEnd(end) };
}

function pad(value: number, length = 2): string {
  return String(value).padStart(length, '0');
}

/** 格式化后端入参时间：默认 `yyyy-MM-dd HH:mm:ss.SSS`（docs/05 第 6 章第 11 条）。 */
export function formatApiTime(value: TimeInput, withMillis = true): string {
  const date = value instanceof Date ? value : new Date(toMs(value));
  const base =
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    ` ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  return withMillis ? `${base}.${pad(date.getMilliseconds(), 3)}` : base;
}

/** 仅日期部分 `yyyy-MM-dd`。 */
export function formatApiDate(value: TimeInput): string {
  const date = value instanceof Date ? value : new Date(toMs(value));
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function toDisplayDate(value: TimeInput | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return parseApiTime(value);
}

/**
 * 界面展示时间：统一转为本地时区 `yyyy-MM-dd HH:mm:ss`。
 * 兼容 ISO-8601（`2026-10-23T19:39:06.854`）、带时区偏移与无时区的数据库格式。
 */
export function formatDisplayTime(value: TimeInput | null | undefined, fallback = '-'): string {
  const date = toDisplayDate(value);
  return date ? formatApiTime(date, false) : fallback;
}

/** 界面展示日期：本地时区 `yyyy-MM-dd`。 */
export function formatDisplayDate(value: TimeInput | null | undefined, fallback = '-'): string {
  const date = toDisplayDate(value);
  return date ? formatApiDate(date) : fallback;
}
